import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class ContentValidatorService extends Service {
  @tracked validationRules = new Map();
  @tracked sanitizationStats = { processed: 0, blocked: 0, sanitized: 0 };

  // Common validation patterns
  VALIDATION_PATTERNS = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    username: /^[a-zA-Z0-9_-]{3,20}$/,
    slug: /^[a-z0-9-]+$/,
    url: /^https?:\/\/.+/,
    hexColor: /^#[0-9A-Fa-f]{6}$/,
    phoneNumber: /^\+?[\d\s-()]{10,20}$/,
  };

  // Content sanitization rules
  SANITIZATION_RULES = {
    allowedHtmlTags: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      'ol',
      'ul',
      'li',
      'a',
      'img',
    ],
    allowedAttributes: {
      a: ['href', 'title'],
      img: ['src', 'alt', 'title', 'width', 'height'],
    },
    maxTextLength: 10000,
    maxUrlLength: 2048,
    forbiddenPatterns: [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /vbscript:/gi,
      /on\w+\s*=/gi,
      /expression\s*\(/gi,
      /eval\s*\(/gi,
      /@import/gi,
      /behavior\s*:/gi,
    ],
  };

  constructor() {
    super(...arguments);
    this.initializeValidationRules();
  }

  initializeValidationRules() {
    // Define validation rules for different content types
    this.addValidationRule('user_profile', {
      username: { required: true, pattern: this.VALIDATION_PATTERNS.username },
      email: { required: true, pattern: this.VALIDATION_PATTERNS.email },
      displayName: { required: true, minLength: 2, maxLength: 50 },
      bio: { maxLength: 500, allowHtml: false },
      website: { pattern: this.VALIDATION_PATTERNS.url, maxLength: 200 },
    });

    this.addValidationRule('article', {
      title: { required: true, minLength: 5, maxLength: 200 },
      slug: { required: true, pattern: this.VALIDATION_PATTERNS.slug },
      content: { required: true, minLength: 50, allowHtml: true },
      excerpt: { maxLength: 300 },
      tags: { type: 'array', maxItems: 10 },
    });

    this.addValidationRule('comment', {
      content: {
        required: true,
        minLength: 1,
        maxLength: 1000,
        allowHtml: false,
      },
      author: { required: true, minLength: 2, maxLength: 50 },
    });

    this.addValidationRule('search_query', {
      q: { required: true, minLength: 1, maxLength: 200 },
      category: { enum: ['articles', 'games', 'streamers', 'tournaments'] },
      limit: { type: 'number', min: 1, max: 100 },
    });
  }

  addValidationRule(contentType, rules) {
    this.validationRules.set(contentType, rules);
  }

  /**
   * Validates content according to defined rules
   * @param {string} contentType - Type of content to validate
   * @param {Object} data - Data to validate
   * @returns {Object} Validation result
   */
  validate(contentType, data) {
    const rules = this.validationRules.get(contentType);
    if (!rules) {
      return { valid: true, errors: [], warnings: [] };
    }

    const errors = [];
    const warnings = [];
    const sanitizedData = { ...data };

    // Validate each field
    Object.entries(rules).forEach(([field, rule]) => {
      const value = data[field];
      const fieldErrors = this.validateField(field, value, rule);

      if (fieldErrors.length > 0) {
        errors.push(...fieldErrors);
      }

      // Sanitize the field if it passes basic validation
      if (fieldErrors.length === 0 && value != null) {
        const sanitized = this.sanitizeField(field, value, rule);
        if (sanitized !== value) {
          sanitizedData[field] = sanitized;
          warnings.push(`Field '${field}' was sanitized`);
        }
      }
    });

    const result = {
      valid: errors.length === 0,
      errors,
      warnings,
      sanitizedData,
    };

    // Update stats
    this.sanitizationStats.processed++;
    if (!result.valid) {
      this.sanitizationStats.blocked++;
    } else if (warnings.length > 0) {
      this.sanitizationStats.sanitized++;
    }

    return result;
  }

  validateField(fieldName, value, rule) {
    const errors = [];

    // Check required fields
    if (rule.required && (value == null || value === '')) {
      errors.push(`Field '${fieldName}' is required`);
      return errors; // Don't continue validation if required field is missing
    }

    // Skip further validation if field is not provided and not required
    if (value == null || value === '') {
      return errors;
    }

    // Type validation
    if (rule.type) {
      if (!this.validateType(value, rule.type)) {
        errors.push(`Field '${fieldName}' must be of type '${rule.type}'`);
        return errors;
      }
    }

    // String validations
    if (typeof value === 'string') {
      // Length validation
      if (rule.minLength && value.length < rule.minLength) {
        errors.push(
          `Field '${fieldName}' must be at least ${rule.minLength} characters long`,
        );
      }

      if (rule.maxLength && value.length > rule.maxLength) {
        errors.push(
          `Field '${fieldName}' must not exceed ${rule.maxLength} characters`,
        );
      }

      // Pattern validation
      if (rule.pattern && !rule.pattern.test(value)) {
        errors.push(`Field '${fieldName}' format is invalid`);
      }

      // Enum validation
      if (rule.enum && !rule.enum.includes(value)) {
        errors.push(
          `Field '${fieldName}' must be one of: ${rule.enum.join(', ')}`,
        );
      }

      // Check for malicious content
      if (this.containsMaliciousContent(value)) {
        errors.push(
          `Field '${fieldName}' contains potentially malicious content`,
        );
      }
    }

    // Number validations
    if (typeof value === 'number') {
      if (rule.min != null && value < rule.min) {
        errors.push(`Field '${fieldName}' must be at least ${rule.min}`);
      }

      if (rule.max != null && value > rule.max) {
        errors.push(`Field '${fieldName}' must not exceed ${rule.max}`);
      }
    }

    // Array validations
    if (Array.isArray(value)) {
      if (rule.maxItems && value.length > rule.maxItems) {
        errors.push(
          `Field '${fieldName}' must not have more than ${rule.maxItems} items`,
        );
      }

      if (rule.minItems && value.length < rule.minItems) {
        errors.push(
          `Field '${fieldName}' must have at least ${rule.minItems} items`,
        );
      }
    }

    return errors;
  }

  validateType(value, expectedType) {
    switch (expectedType) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' && !isNaN(value);
      case 'boolean':
        return typeof value === 'boolean';
      case 'array':
        return Array.isArray(value);
      case 'object':
        return (
          typeof value === 'object' && value !== null && !Array.isArray(value)
        );
      default:
        return true; // Unknown type, allow it
    }
  }

  sanitizeField(fieldName, value, rule) {
    if (typeof value !== 'string') {
      return value; // Only sanitize strings
    }

    let sanitized = value;

    // HTML sanitization
    if (rule.allowHtml) {
      sanitized = this.sanitizeHtml(sanitized);
    } else {
      // Strip all HTML tags if HTML is not allowed
      sanitized = this.stripHtml(sanitized);
    }

    // Remove forbidden patterns
    sanitized = this.removeForbiddenPatterns(sanitized);

    // Trim whitespace
    sanitized = sanitized.trim();

    // Truncate if necessary
    if (rule.maxLength && sanitized.length > rule.maxLength) {
      sanitized = sanitized.substring(0, rule.maxLength);
    }

    return sanitized;
  }

  sanitizeHtml(html) {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove forbidden elements and attributes
    this.cleanHtmlElement(tempDiv);

    return tempDiv.innerHTML;
  }

  cleanHtmlElement(element) {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_ELEMENT,
      null,
      false,
    );

    const nodesToRemove = [];
    let node;

    while ((node = walker.nextNode())) {
      const tagName = node.tagName.toLowerCase();

      // Remove disallowed tags
      if (!this.SANITIZATION_RULES.allowedHtmlTags.includes(tagName)) {
        nodesToRemove.push(node);
        continue;
      }

      // Clean attributes
      const allowedAttrs =
        this.SANITIZATION_RULES.allowedAttributes[tagName] || [];
      const attrs = Array.from(node.attributes);

      attrs.forEach((attr) => {
        if (!allowedAttrs.includes(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
        } else {
          // Sanitize attribute values
          const sanitizedValue = this.sanitizeAttributeValue(attr.value);
          if (sanitizedValue !== attr.value) {
            node.setAttribute(attr.name, sanitizedValue);
          }
        }
      });
    }

    // Remove flagged nodes
    nodesToRemove.forEach((node) => {
      node.parentNode?.removeChild(node);
    });
  }

  sanitizeAttributeValue(value) {
    // Remove javascript: and other dangerous protocols
    const dangerousProtocols = /^(javascript|vbscript|data|file):/i;
    if (dangerousProtocols.test(value)) {
      return '';
    }

    // Clean up the value
    return value.replace(/[<>"']/g, '');
  }

  stripHtml(text) {
    // Simple HTML tag removal
    return text.replace(/<[^>]*>/g, '');
  }

  removeForbiddenPatterns(text) {
    let cleaned = text;

    this.SANITIZATION_RULES.forbiddenPatterns.forEach((pattern) => {
      cleaned = cleaned.replace(pattern, '');
    });

    return cleaned;
  }

  containsMaliciousContent(content) {
    // Check for common XSS and injection patterns
    const maliciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /vbscript:/gi,
      /on\w+\s*=/gi,
      /expression\s*\(/gi,
      /eval\s*\(/gi,
      /document\.cookie/gi,
      /window\.location/gi,
      /<iframe[^>]*>/gi,
      /<embed[^>]*>/gi,
      /<object[^>]*>/gi,
    ];

    return maliciousPatterns.some((pattern) => pattern.test(content));
  }

  /**
   * Quick sanitization for user inputs
   * @param {string} input - User input to sanitize
   * @param {Object} options - Sanitization options
   * @returns {string} Sanitized input
   */
  quickSanitize(input, options = {}) {
    if (typeof input !== 'string') {
      return input;
    }

    let sanitized = input;

    // Remove/escape HTML by default
    if (options.allowHtml) {
      sanitized = this.sanitizeHtml(sanitized);
    } else {
      sanitized = this.stripHtml(sanitized);
    }

    // Remove forbidden patterns
    sanitized = this.removeForbiddenPatterns(sanitized);

    // Trim and limit length
    sanitized = sanitized.trim();
    if (options.maxLength) {
      sanitized = sanitized.substring(0, options.maxLength);
    }

    return sanitized;
  }

  /**
   * Validates and sanitizes GraphQL input
   * @param {Object} input - GraphQL variables/input
   * @returns {Object} Validation and sanitization result
   */
  validateGraphQLInput(input) {
    const errors = [];
    const sanitized = {};

    Object.entries(input).forEach(([key, value]) => {
      // Basic type checking
      if (typeof value === 'string') {
        if (this.containsMaliciousContent(value)) {
          errors.push(`GraphQL input '${key}' contains malicious content`);
        } else {
          sanitized[key] = this.quickSanitize(value, { maxLength: 1000 });
        }
      } else if (typeof value === 'number') {
        // Validate reasonable number ranges
        if (!isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER) {
          errors.push(`GraphQL input '${key}' has invalid number value`);
        } else {
          sanitized[key] = value;
        }
      } else {
        sanitized[key] = value;
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      sanitizedInput: sanitized,
    };
  }

  /**
   * Gets sanitization statistics
   * @returns {Object} Sanitization stats
   */
  getSanitizationStats() {
    return { ...this.sanitizationStats };
  }

  /**
   * Resets sanitization statistics
   */
  resetStats() {
    this.sanitizationStats = { processed: 0, blocked: 0, sanitized: 0 };
  }
}
